import { useRef, useEffect, useCallback, useState } from 'react';
import { Capacitor } from '@capacitor/core';

export const REFRESHED_TS_KEY = 'refreshedTimeStamp';

const PULL_THRESHOLD = 70;
const MAX_PULL = 120;
// Minimum time (ms) the refresh lock is held once a pull-refresh fires. Most screens
// pass a synchronous onRefresh (e.g. `setRefreshNonce(n => n + 1)` or a bare
// `dispatch(...)`), so the returned promise resolves on the same tick — long before
// the underlying saga/API has actually finished. Without a floor, the lock releases
// instantly and a second pull can stack on top of the first. Holding the lock for at
// least this long keeps `isRefreshing` true (spinner stays up, onTouchStart stays
// blocked) until the in-flight refresh is realistically over.
const MIN_REFRESH_MS = 1000;

const wait = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));
// Treat anything within this many px of the top as "at the top". iOS rubber-band
// bounce-back can leave a tiny non-zero (or transiently negative) scrollTop; the
// epsilon prevents that from being read as "scrolled away from the top".
const SCROLL_EPSILON = 2;
// Total finger travel (px) before we commit to an axis. Below this we don't know
// yet whether the gesture is a vertical pull or a horizontal swipe.
const AXIS_SLOP = 8;

export function usePullToRefresh({ onRefresh, targetRef, disabled }: {
    onRefresh: () => void | Promise<void>;
    targetRef?: React.RefObject<HTMLElement>;
    disabled?: boolean;
}) {
    const isNative = Capacitor.isNativePlatform();
    const scrollRef = useRef<HTMLDivElement>(null);
    const startYRef = useRef(0);
    const startXRef = useRef(0);
    const pullingRef = useRef(false);
    // Gesture axis once enough travel has accumulated: 'pending' until we cross
    // AXIS_SLOP, then 'vertical' (PTR candidate) — a horizontal gesture instead
    // cancels pulling outright so we never hijack carousels / swipeable rows.
    const axisRef = useRef<'pending' | 'vertical'>('pending');
    const pullDistanceRef = useRef(0);
    const isRefreshingRef = useRef(false);
    const [pullDistance, setPullDistanceState] = useState(0);
    const [isRefreshing, setIsRefreshingState] = useState(false);

    const setPullDistance = useCallback((d: number) => {
        pullDistanceRef.current = d;
        setPullDistanceState(d);
    }, []);

    const handleRefresh = useCallback(async () => {
        if (isRefreshingRef.current) return;
        isRefreshingRef.current = true;
        setIsRefreshingState(true);
        pullDistanceRef.current = 0;
        setPullDistanceState(0);
        const startedAt = Date.now();
        try {
            await onRefresh();
        } finally {
            // Hold the lock for at least MIN_REFRESH_MS. onRefresh often resolves on the
            // same tick (synchronous dispatch), so without this floor the lock would
            // release before the in-flight fetch is over, letting a second pull stack.
            const elapsed = Date.now() - startedAt;
            if (elapsed < MIN_REFRESH_MS) await wait(MIN_REFRESH_MS - elapsed);
            localStorage.setItem(REFRESHED_TS_KEY, new Date().toISOString());
            isRefreshingRef.current = false;
            setIsRefreshingState(false);
        }
    }, [onRefresh]);

    useEffect(() => {
        if (!isNative || disabled) return;

        // Listeners on the full screen so the non-scrollable header zone is covered.
        const target = targetRef?.current ?? document.body;

        // Effective distance from the very top of the content. Some screens scroll the
        // ref'd container internally; others (Phone wrapper has min-height:100vh, so on
        // tall content the page grows and the DOCUMENT scrolls while the inner div's
        // scrollTop stays 0) scroll the document. Take the max so "at top" is true only
        // when BOTH are at the top — i.e. there is genuinely nothing above to scroll to.
        const effectiveScrollTop = () => {
            const elTop = scrollRef.current ? scrollRef.current.scrollTop : 0;
            const docTop = window.scrollY
                || document.documentElement.scrollTop
                || document.body.scrollTop
                || 0;
            return Math.max(elTop, docTop);
        };

        const cancelPull = () => {
            pullingRef.current = false;
            axisRef.current = 'pending';
            if (pullDistanceRef.current !== 0) setPullDistance(0);
        };

        const onTouchStart = (e: TouchEvent) => {
            // Block PTR if the scroll container is not mounted (e.g. a drill-down view
            // replaced the main screen) — prevents e.preventDefault() from eating
            // scroll gestures in child scrollable containers.
            if (!scrollRef.current) return;
            // Lock out new pulls while a refresh is already in flight (released in
            // handleRefresh's finally on both success and error).
            if (isRefreshingRef.current) return;
            // Ignore multi-touch (pinch/zoom, two-finger gestures).
            if (e.touches.length !== 1) return;
            // GATE PURELY ON SCROLL POSITION, NOT TOUCH LOCATION.
            // PTR engages only when the scroll container is at (or within epsilon of)
            // the very top — regardless of whether the touch lands on the header, the
            // middle, or the bottom of the viewport. While scrolled down, every
            // downward drag is a normal scroll-up; we don't intercept it. Because this
            // is decided once, at touch-down, a gesture that *reaches* the top mid-drag
            // does NOT engage PTR — a fresh touch-down is required after reaching top.
            if (effectiveScrollTop() > SCROLL_EPSILON) return;
            startYRef.current = e.touches[0].clientY;
            startXRef.current = e.touches[0].clientX;
            pullingRef.current = true;
            axisRef.current = 'pending';
        };

        const onTouchMove = (e: TouchEvent) => {
            if (!pullingRef.current) return;
            // A second finger landed mid-gesture — bail out (don't fight pinch/zoom).
            if (e.touches.length !== 1) { cancelPull(); return; }

            const dy = e.touches[0].clientY - startYRef.current;
            const dx = e.touches[0].clientX - startXRef.current;

            // Decide the axis once the finger has travelled enough to be unambiguous.
            // A horizontal-dominant gesture (carousel / swipeable row / nested
            // horizontal ScrollView) cancels PTR so we never hijack it.
            if (axisRef.current === 'pending') {
                if (Math.max(Math.abs(dx), Math.abs(dy)) < AXIS_SLOP) return;
                if (Math.abs(dx) > Math.abs(dy)) { cancelPull(); return; }
                axisRef.current = 'vertical';
            }

            // Upward drag while at the top → not a pull; release.
            if (dy <= 0) { cancelPull(); return; }
            if (dy > 10) e.preventDefault();
            setPullDistance(Math.min(dy, MAX_PULL));
        };

        const onTouchEnd = () => {
            if (!pullingRef.current) return;
            pullingRef.current = false;
            axisRef.current = 'pending';
            if (pullDistanceRef.current >= PULL_THRESHOLD && !isRefreshingRef.current) {
                handleRefresh();
            } else {
                setPullDistance(0);
            }
        };

        // OS-interrupted gesture (call, notification, etc.) — reset cleanly.
        const onTouchCancel = () => cancelPull();

        target.addEventListener('touchstart', onTouchStart, { passive: true });
        target.addEventListener('touchmove', onTouchMove, { passive: false });
        target.addEventListener('touchend', onTouchEnd, { passive: true });
        target.addEventListener('touchcancel', onTouchCancel, { passive: true });

        return () => {
            target.removeEventListener('touchstart', onTouchStart);
            target.removeEventListener('touchmove', onTouchMove);
            target.removeEventListener('touchend', onTouchEnd);
            target.removeEventListener('touchcancel', onTouchCancel);
        };
    }, [isNative, handleRefresh, targetRef, setPullDistance, disabled]);

    return { scrollRef, pullDistance, isRefreshing };
}
